/**
 * Position Detail Screen Component
 *
 * Displays position details with financial metrics, thesis summary, and activity timeline.
 * Includes floating action button for adding position comments.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';
import { getPosition } from '../../services/portfolioApi';
import { deleteCorporateAction } from '../../services/tradesApi';
import {
  deleteCorporateActionById,
  updateCorporateActionDescription,
} from '../../services/tradesApi';
import type { SplitConsolidateResponse } from '../../services/tradesApi';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { getLocalizedInstrumentName } from '../../utils/translationHelpers';
import { showConfirm, showSuccess, showError } from '../../utils/alert';
import { useDeletePosition } from '../../services/api/queries/usePortfolio';
import { useCaptureWatchlist } from '../../services/queries/useWatchlist';
import { PositionMetricsCard } from '../../components/portfolio/PositionMetricsCard';
import { PositionThesisSummary } from '../../components/portfolio/PositionThesisSummary';
import { TargetPriceSummary } from '../../components/portfolio/TargetPriceSummary';
import { PositionRiskCard } from '../../components/portfolio/PositionRiskCard';
import { ViewOverrideList } from '../../components/portfolio/ViewOverrideList';
import { ActivityTimeline } from '../../components/portfolio/ActivityTimeline';
import { FloatingAddButton } from '../../components/portfolio/FloatingAddButton';
import { PositionCommentForm } from '../../components/portfolio/PositionCommentForm';
import { CorporateActionEditModal } from '../../components/portfolio/CorporateActionEditModal';
import type { PositionComment } from '../../services/api/positionCommentApi';
import type { Trade } from '../../services/portfolioApi';

type PositionDetailNavigationProp = StackNavigationProp<PortfolioNavigatorParamList>;

export function PositionDetailScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<PositionDetailNavigationProp>();
  const route = useRoute<RouteProp<PortfolioNavigatorParamList, 'PositionDetail'>>();
  const { positionId } = route.params;
  const queryClient = useQueryClient();
  const deletePosition = useDeletePosition();
  const captureWatchlist = useCaptureWatchlist();

  const [position, setPosition] = useState<any>(null);
  const [positionLoading, setPositionLoading] = useState(true);
  const [positionError, setPositionError] = useState<string | null>(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [editingComment, setEditingComment] = useState<PositionComment | null>(null);
  const [showCAModal, setShowCAModal] = useState(false);
  const [selectedCA, setSelectedCA] = useState<SplitConsolidateResponse | null>(null);
  const [caDeleting, setCADeleting] = useState(false);

  // UI fix #1: delete directly from the position level, no need to enter
  // edit-financial-metrics first.
  const handleDeletePosition = useCallback(async () => {
    if (!position) return;
    const confirmed = await showConfirm(
      t('portfolio.deletePosition', { defaultValue: 'Delete Position' }),
      t('portfolio.deleteConfirm', {
        defaultValue: 'Are you sure you want to delete {symbol}? This action cannot be undone.',
        symbol: position.symbol,
      }),
      {
        confirmText: t('common.delete', { defaultValue: 'Delete' }),
        cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
        destructiveAction: true,
      }
    );
    if (!confirmed) return;
    try {
      await deletePosition.mutateAsync(position.id);
      showSuccess(
        t('portfolio.positionDeleted', { defaultValue: 'Position deleted successfully!' })
      );
      navigation.popToTop();
    } catch (e: any) {
      showError(
        e?.message || t('portfolio.deleteFailed', { defaultValue: 'Failed to delete position' })
      );
    }
  }, [position, deletePosition, navigation, t]);

  // UI fix #3: a previously-owned stock sold down to 0 can go back on the
  // watchlist straight from the position level.
  const handleAddToWatchlist = useCallback(async () => {
    if (!position) return;
    try {
      await captureWatchlist.mutateAsync({ symbol: position.symbol });
      showSuccess(
        t('watchlist.added', {
          defaultValue: '{symbol} added to watchlist',
          symbol: position.symbol,
        })
      );
    } catch (e: any) {
      showError(
        e?.message || t('watchlist.addFailed', { defaultValue: 'Failed to add to watchlist' })
      );
    }
  }, [position, captureWatchlist, t]);

  // Set header button based on instrument link status
  useEffect(() => {
    if (!position) return;

    const isLinked = position.instrument_id !== null;

    const isZeroBalance = Number(position.quantity) === 0;
    navigation.setOptions({
      headerRight: () => (
        <View style={styles.headerActions}>
          {isZeroBalance && (
            <TouchableOpacity
              onPress={handleAddToWatchlist}
              disabled={captureWatchlist.isPending}
              style={[styles.headerButton, { backgroundColor: theme.colors.backgroundElevated }]}
            >
              <Text style={[styles.headerButtonText, { color: theme.colors.text }]}>
                {t('watchlist.addShort', { defaultValue: 'Watch' })}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={handleDeletePosition}
            disabled={deletePosition.isPending}
            style={[styles.headerButton, { backgroundColor: theme.colors.error }]}
          >
            <Text style={[styles.headerButtonText, { color: theme.colors.textInverse }]}>
              {t('common.delete', { defaultValue: 'Delete' })}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              if (isLinked) {
                // Navigate to instrument details for linked positions
                navigation.navigate('InstrumentDetail', {
                  symbol: position.symbol,
                  currency: position.currency,
                  instrument_id: position.instrument_id,
                });
              } else {
                // Navigate to instrument detail for linking unmatched positions
                navigation.navigate('InstrumentDetail', {
                  symbol: position.symbol,
                  currency: position.currency || 'USD',
                  position_id: position.id,
                });
              }
            }}
            style={[styles.headerButton, { backgroundColor: theme.colors.primary }]}
          >
            <Text style={[styles.headerButtonText, { color: theme.colors.textInverse }]}>
              {isLinked
                ? t('portfolio.details', { defaultValue: 'Details' })
                : t('portfolio.linkInstrument', { defaultValue: 'Link' })}
            </Text>
          </TouchableOpacity>
        </View>
      ),
    });
  }, [
    position,
    navigation,
    t,
    theme,
    handleDeletePosition,
    handleAddToWatchlist,
    captureWatchlist.isPending,
    deletePosition.isPending,
  ]);

  // Fetch position details - refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      const fetchPosition = async () => {
        try {
          setPositionLoading(true);
          const data = await getPosition(positionId);
          setPosition(data);
        } catch (error: any) {
          setPositionError(
            error.message ||
              t('portfolio.failedToLoadPosition', { defaultValue: 'Failed to load position' })
          );
        } finally {
          setPositionLoading(false);
        }
      };
      fetchPosition();
    }, [positionId])
  );

  const handleAddComment = () => {
    setEditingComment(null);
    setShowCommentModal(true);
  };

  const handleEditComment = (comment: PositionComment) => {
    setEditingComment(comment);
    setShowCommentModal(true);
  };

  const handleEditTrade = (trade: Trade) => {
    navigation.navigate('AddTrade', { tradeId: trade.id });
  };

  const handleDeleteCorporateAction = async (trade: Trade) => {
    try {
      await deleteCorporateAction(trade.id);

      // Invalidate and refetch queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['positionTrades'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['positions'] });

      // Also refetch the position data
      const data = await getPosition(positionId);
      setPosition(data);
    } catch (error: any) {
      throw error; // Re-throw to let the ActivityTimeline handle the error display
    }
  };

  const handleEditCorporateAction = (ca: SplitConsolidateResponse) => {
    setSelectedCA(ca);
    setShowCAModal(true);
  };

  const handleDeleteCA = async (ca: SplitConsolidateResponse) => {
    setCADeleting(true);
    try {
      await deleteCorporateActionById(ca.id);

      // Invalidate and refetch queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['corporateActions', positionId] });
      queryClient.invalidateQueries({ queryKey: ['positionTrades', position.symbol] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['positions'] });

      // Also refetch the position data
      const data = await getPosition(positionId);
      setPosition(data);

      // Close the modal
      setShowCAModal(false);
      setSelectedCA(null);
    } catch (error: any) {
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setCADeleting(false);
    }
  };

  const handleCloseCAModal = () => {
    setShowCAModal(false);
    setSelectedCA(null);
  };

  const handleSaveCA = async (caId: number, description: string) => {
    try {
      await updateCorporateActionDescription(caId, description);

      // Update the local state to reflect the saved description
      if (selectedCA && selectedCA.id === caId) {
        setSelectedCA({ ...selectedCA, description });
      }

      // Invalidate and refetch queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['corporateActions', positionId] });
      queryClient.invalidateQueries({ queryKey: ['positionTrades', position?.symbol] });

      // Refetch position data
      if (position) {
        const data = await getPosition(positionId);
        setPosition(data);
      }

      // Close the modal after successful save
      setShowCAModal(false);
      setSelectedCA(null);
    } catch (error: any) {
      // Re-throw to let the modal handle the error display
      throw error;
    }
  };

  const handleCloseCommentModal = () => {
    setShowCommentModal(false);
    setEditingComment(null);
  };

  // Loading state
  if (positionLoading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('portfolio.loadingPosition', { defaultValue: 'Loading position...' })}
        </Text>
      </View>
    );
  }

  // Error state
  if (positionError || !position) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorTitle, { color: theme.colors.error }]}>
          {t('common.error', { defaultValue: 'Error' })}
        </Text>
        <Text style={[styles.errorText, { color: theme.colors.textSecondary }]}>
          {positionError ||
            t('portfolio.failedToLoadPosition', { defaultValue: 'Failed to load position' })}
        </Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
        nestedScrollEnabled
      >
        {/* Financial Metrics - Compact */}
        <PositionMetricsCard position={position} />

        {/* Position Thesis - Summary with Edit button */}
        <PositionThesisSummary
          positionId={position.id}
          currency={position.currency}
          symbol={position.instrument_symbol || position.symbol}
          companyName={position.instrument_name || position.company_name}
        />

        {/* Saved target price (issue 3): the builder's Save lands here —
            weighted target + scenarios + rationale, editable via the builder. */}
        <TargetPriceSummary
          positionId={position.id}
          symbol={position.instrument_symbol || position.symbol}
        />

        {/* Risk-limit monitor - current-book measurements from the weekly run */}
        <PositionRiskCard positionId={position.id} />

        {/* View Overrides - User-authored notes */}
        <ViewOverrideList symbol={position.symbol} />

        {/* Activity Timeline - Unified */}
        <ActivityTimeline
          positionId={position.id}
          symbol={position.symbol}
          currency={position.currency}
          onEditComment={handleEditComment}
          onEditTrade={handleEditTrade}
          onEditCorporateAction={handleEditCorporateAction}
          onDeleteCorporateAction={handleDeleteCorporateAction}
        />

        {/* Extra padding at bottom for floating button */}
        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Floating Add Button with Callouts */}
      <FloatingAddButton
        onAddComment={handleAddComment}
        onAddTrade={(symbol, currency) => {
          navigation.navigate('AddTrade', {
            symbol,
            currency,
            instrumentName: getLocalizedInstrumentName(
              position.instrument_name,
              position.instrument_name_sch,
              position.instrument_name_tch,
              language
            ),
          });
        }}
        onAddCorporateAction={() => {
          navigation.navigate('CorporateAction', {
            positionId,
            symbol: position.symbol,
            instrumentName: getLocalizedInstrumentName(
              position.instrument_name,
              position.instrument_name_sch,
              position.instrument_name_tch,
              language
            ),
          });
        }}
        symbol={position.symbol}
        currency={position.currency}
      />

      {/* Comment Modal */}
      <Modal
        visible={showCommentModal}
        animationType="slide"
        onRequestClose={handleCloseCommentModal}
      >
        <View style={[styles.modalContainer, { backgroundColor: theme.colors.background }]}>
          <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              {editingComment
                ? t('portfolio.positionComment.editComment', { defaultValue: 'Edit Comment' })
                : t('portfolio.positionComment.addComment', { defaultValue: 'Add Comment' })}
            </Text>
            <TouchableOpacity
              onPress={handleCloseCommentModal}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </TouchableOpacity>
          </View>
          <PositionCommentForm
            positionId={position.id}
            existingComment={editingComment || undefined}
            onSuccess={handleCloseCommentModal}
          />
        </View>
      </Modal>

      {/* Corporate Action Edit Modal */}
      <CorporateActionEditModal
        visible={showCAModal}
        corporateAction={selectedCA}
        onClose={handleCloseCAModal}
        onSave={handleSaveCA}
        onDelete={handleDeleteCA}
        loading={caDeleting}
      />
    </>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      marginTop: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
    },
    errorTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.sm,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    errorText: {
      fontSize: theme.typography.fontSizeMd,
      textAlign: 'center',
    },
    bottomPadding: {
      height: 100,
    },
    modalContainer: {
      flex: 1,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      paddingTop: Platform.OS === 'ios' ? 50 : 12,
    },
    modalTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerButton: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderRadius: theme.radii.md,
      marginRight: theme.spacing.sm,
    },
    headerButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
